import { L4Map } from './l4-map';
import type { BreakdownCell, BreakdownTableIndex } from './types';
import type { ReactNode } from 'react';

interface OrderingOptions {
  columnOrder?: string[];
  subColumnOrder?: string[];
  rowOrder?: string[];
  subRowOrder?: string[];
}

/**
 * Sort an array of values based on a custom order array
 * Values not in the order array are appended at the end (sorted alphabetically)
 */
function sortByOrder<T extends string | null>(
  values: T[],
  order?: string[],
): T[] {
  if (!order || order.length === 0) {
    // Default sorting
    return values.sort((a, b) => {
      if (a === null) return 1;
      if (b === null) return -1;
      return (a as string).localeCompare(b as string);
    });
  }

  const ordered: T[] = [];
  const unordered: T[] = [];
  const orderSet = new Set(order);

  // Separate ordered and unordered values
  values.forEach((value) => {
    if (value === null) {
      unordered.push(value);
    } else if (orderSet.has(value)) {
      ordered.push(value);
    } else {
      unordered.push(value);
    }
  });

  // Sort ordered values by their position in the order array
  ordered.sort((a, b) => {
    const aIndex = order.indexOf(a as string);
    const bIndex = order.indexOf(b as string);
    return aIndex - bIndex;
  });

  // Sort unordered values alphabetically
  unordered.sort((a, b) => {
    if (a === null) return 1;
    if (b === null) return -1;
    return (a as string).localeCompare(b as string);
  });

  // Combine: ordered first, then unordered (with nulls at the end)
  const nulls = unordered.filter((v) => v === null);
  const nonNulls = unordered.filter((v) => v !== null);
  return [...ordered, ...nonNulls, ...nulls];
}

/**
 * Builds an optimized index structure for O(1) cell lookups
 * and efficient table rendering
 */
export function buildTableIndex(
  data: BreakdownCell[],
  ordering?: OrderingOptions,
): BreakdownTableIndex {
  // Validation: Check if data has the correct schema
  if (data.length > 0) {
    const firstCell = data[0];
    const hasInvalidSchema =
      firstCell.column === undefined ||
      firstCell.row === undefined ||
      firstCell.value === undefined;

    if (hasInvalidSchema) {
      const sampleKeys = Object.keys(firstCell);
      console.warn(
        '[BreakdownTable] Invalid data schema detected. Expected fields: column, subColumn, row, subRow, value',
        '\nReceived fields:',
        sampleKeys,
        '\nSample cell:',
        firstCell,
        '\n\nThis usually means data needs to be normalized before passing to BreakdownTable.',
        '\nExample normalization:',
        '\n  const normalizedData = rawData.map(d => ({',
        '\n    column: d.month,           // x-axis primary',
        '\n    subColumn: d.metric ?? null,  // x-axis secondary',
        '\n    row: d.supplier,           // y-axis primary',
        '\n    subRow: d.branch ?? null,  // y-axis secondary',
        '\n    value: d.amount,           // numeric or display value',
        '\n  }));',
      );
    }

    // Check for cells with undefined column/row (will result in empty cellMap)
    const invalidCells = data.filter(
      (cell) => cell.column === undefined || cell.row === undefined,
    );
    if (invalidCells.length > 0) {
      console.warn(
        `[BreakdownTable] Found ${invalidCells.length} cells with undefined column or row.`,
        'These cells will be ignored, resulting in empty aggregations.',
        '\nFirst invalid cell:',
        invalidCells[0],
      );
    }
  }

  // Step 1: Extract unique values for each dimension
  const columnSet = new Set<string>();
  const subColumnSet = new Set<string | null>();
  const rowSet = new Set<string>();
  const subRowSet = new Set<string | null>();

  data.forEach((cell) => {
    // Skip invalid cells (with undefined column/row) to prevent "undefined|null|undefined|null" keys
    if (cell.column === undefined || cell.row === undefined) {
      return;
    }
    columnSet.add(cell.column);
    subColumnSet.add(cell.subColumn ?? null);
    rowSet.add(cell.row);
    // Use subRowKey for uniqueness if provided, otherwise use subRow
    subRowSet.add(cell.subRowKey ?? cell.subRow ?? null);
  });

  // Step 2: Create ordered arrays (use custom order if provided, otherwise sort)
  const columnValues = sortByOrder(
    Array.from(columnSet),
    ordering?.columnOrder,
  );
  const subColumnValues = sortByOrder(
    Array.from(subColumnSet),
    ordering?.subColumnOrder,
  );
  const rowValues = sortByOrder(Array.from(rowSet), ordering?.rowOrder);
  const subRowValues = sortByOrder(
    Array.from(subRowSet),
    ordering?.subRowOrder,
  );

  // Step 3: Create index maps (dimension value → array index)
  const columnIndex = new Map<string, number>();
  const subColumnIndex = new Map<string | null, number>();
  const rowIndex = new Map<string, number>();
  const subRowIndex = new Map<string | null, number>();

  columnValues.forEach((v, i) => columnIndex.set(v, i));
  subColumnValues.forEach((v, i) => subColumnIndex.set(v, i));
  rowValues.forEach((v, i) => rowIndex.set(v, i));
  subRowValues.forEach((v, i) => subRowIndex.set(v, i));

  // Step 4: Build hierarchy maps (for detecting grouping structure)
  const columnHierarchy = new Map<string, Set<string | null>>();
  const rowHierarchy = new Map<string, Set<string | null>>();

  data.forEach((cell) => {
    // Skip invalid cells
    if (cell.column === undefined || cell.row === undefined) {
      return;
    }
    // Column hierarchy: column → Set of subColumn values
    const colSet = columnHierarchy.get(cell.column) ?? new Set<string | null>();
    colSet.add(cell.subColumn ?? null);
    columnHierarchy.set(cell.column, colSet);

    // Row hierarchy: row → Set of subRow values (use subRowKey for uniqueness)
    const rowSet = rowHierarchy.get(cell.row) ?? new Set<string | null>();
    rowSet.add(cell.subRowKey ?? cell.subRow ?? null);
    rowHierarchy.set(cell.row, rowSet);
  });

  // Step 5: Build flat map for O(1) cell lookup and subRowKey mapping
  const cellMap = new L4Map<BreakdownCell>();
  const subRowKeyToDisplay = new Map<string, string>();

  data.forEach((cell) => {
    // Skip invalid cells to prevent "undefined|null|undefined|null" keys
    if (cell.column === undefined || cell.row === undefined) {
      return;
    }
    // Use subRowKey for key generation if provided, otherwise use subRow
    const subRowForKey = cell.subRowKey ?? cell.subRow ?? null;
    cellMap.set(
      cell.column,
      cell.subColumn ?? null,
      cell.row,
      subRowForKey,
      cell,
    );

    // Map subRowKey to display value (subRow) for rendering
    if (cell.subRowKey && cell.subRow) {
      subRowKeyToDisplay.set(cell.subRowKey, cell.subRow);
    }
  });

  const yoyCells = data.filter(
    (c) => c.metadata?.isPreviousYear || c.metadata?.isDelta,
  );
  if (yoyCells.length > 0) {
    console.log('[buildTableIndex] YOY cells in cellMap:', {
      yoyCellCount: yoyCells.length,
      parentYOYCells: yoyCells.filter((c) => c.subColumn === null).length,
      childYOYCells: yoyCells.filter((c) => c.subColumn !== null).length,
      sampleYOYKeys: yoyCells.slice(0, 5).map((cell) => ({
        key: `${cell.column}|${cell.subColumn ?? null}|${cell.row}|${cell.subRow ?? null}`,
        column: cell.column,
        subColumn: cell.subColumn,
        row: cell.row,
        value: cell.value,
        isPrevYear: cell.metadata?.isPreviousYear,
        isDelta: cell.metadata?.isDelta,
      })),
    });
  }

  return {
    columnValues,
    subColumnValues,
    rowValues,
    subRowValues,
    columnIndex,
    subColumnIndex,
    rowIndex,
    subRowIndex,
    cellMap,
    columnHierarchy,
    rowHierarchy,
    subRowKeyToDisplay,
  };
}

/**
 * Get a cell by its 4D coordinates
 */
export function getCell(
  index: BreakdownTableIndex,
  column: string,
  subColumn: string | null,
  row: string,
  subRow: string | null,
): BreakdownCell | undefined {
  const cell = index.cellMap.get(column, subColumn, row, subRow);

  return cell;
}

/**
 * Check if a dimension has sub-dimensions
 */
export function hasSubDimensions(
  index: BreakdownTableIndex,
  dimension: 'column' | 'row',
  value: string,
): boolean {
  if (dimension === 'column') {
    const subColumnSet = index.columnHierarchy.get(value);
    if (!subColumnSet) return false;
    // Has sub-dimensions if there are any non-null values
    // (size > 1 means null + at least one value, OR size === 1 with non-null value)
    return (
      subColumnSet.size > 1 ||
      (subColumnSet.size === 1 && !subColumnSet.has(null))
    );
  } else {
    const subRowSet = index.rowHierarchy.get(value);
    if (!subRowSet) return false;
    // Has sub-dimensions if there are any non-null values
    return subRowSet.size > 1 || (subRowSet.size === 1 && !subRowSet.has(null));
  }
}

/**
 * Get sub-dimension values for a given parent dimension value
 * Uses custom ordering from the index if available
 */
export function getSubDimensions(
  index: BreakdownTableIndex,
  dimension: 'column' | 'row',
  parentValue: string,
  ordering?: OrderingOptions,
): (string | null)[] {
  if (dimension === 'column') {
    const subColumnSet = index.columnHierarchy.get(parentValue);
    if (!subColumnSet) return [];
    const values = Array.from(subColumnSet).filter(
      (v) => v !== null,
    ) as string[];
    return sortByOrder(values, ordering?.subColumnOrder);
  } else {
    const subRowSet = index.rowHierarchy.get(parentValue);
    if (!subRowSet) return [];
    const values = Array.from(subRowSet).filter((v) => v !== null) as string[];
    return sortByOrder(values, ordering?.subRowOrder);
  }
}

/**
 * Check if a value is a YOY object
 */
export function isYOYValue(
  value:
    | number
    | string
    | ReactNode
    | { current: number; previous: number; delta: number }
    | null,
): value is { current: number; previous: number; delta: number } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'current' in value &&
    'previous' in value &&
    'delta' in value &&
    typeof value.current === 'number' &&
    typeof value.previous === 'number' &&
    typeof value.delta === 'number'
  );
}

/**
 * Extract numeric value from a cell value
 * Handles YOY objects by extracting the current value
 */
export function extractNumericValue(
  value:
    | number
    | string
    | ReactNode
    | { current: number; previous: number; delta: number }
    | null,
): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  }
  if (isYOYValue(value)) {
    return value.current;
  }
  return 0;
}

/**
 * Aggregate YOY objects by summing current, previous, and calculating delta
 */
export function aggregateYOYValues(
  values: { current: number; previous: number; delta: number }[],
): { current: number; previous: number; delta: number } {
  const current = values.reduce((sum, v) => sum + v.current, 0);
  const previous = values.reduce((sum, v) => sum + v.previous, 0);
  const delta = current - previous;
  return { current, previous, delta };
}
